import { useForm } from '@tanstack/react-form'
import { useAddExpenseMutation } from '../hooks'
import { useExpensesStore } from '../store/expensesStore'
import { ExpenseCreateSchema } from '../schema/expensesSchema'
import { ExpenseItemsTable } from './ExpenseItemsTable'
import type {
  ExpenseCreateData,
  ExpenseItemInput,
} from '../schema/expensesSchema'
import type { Church } from '@/features/churches/types/church'
import type { ExpensesCategory } from '../types/expenses'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import DatePicker from '@/components/custom/date-picker'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { transformDateToTimezone } from '@/utils/timeZoneUtils'

const defaultValues: ExpenseCreateData = {
  churchId: '',
  requester: '',
  requesterEmail: '',
  amount: 0,
  date: '',
  notes: '',
  expenseItems: [],
}

interface ExpenseCreateFormProps {
  internal?: boolean
  publicChurches?: Array<Church>
  publicCategories?: Array<ExpensesCategory>
  onClose?: () => void
  onSuccess?: () => void
}

export function ExpenseCreateForm({
  internal,
  publicChurches,
  publicCategories,
  onClose,
  onSuccess,
}: ExpenseCreateFormProps) {
  const { mutateAsync: addExpenseMutation, isPending } = useAddExpenseMutation()

  const churches = internal
    ? useChurchesStore((s) => s.churches)
    : publicChurches || []
  const categories = internal
    ? useExpensesStore((s) => s.categories)
    : publicCategories || []

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      const totalAmount = value.expenseItems.reduce(
        (sum, item) => sum + item.amount,
        0,
      )

      const transformedValue = {
        ...value,
        amount: totalAmount,
        date: transformDateToTimezone(value.date),
      }

      try {
        await addExpenseMutation({
          expense: transformedValue as ExpenseCreateData,
        })
        form.reset()
        if (internal) {
          onClose?.()
        } else {
          onSuccess?.()
        }
      } catch (error) {
        console.error('Unexpected error submitting expense form:', error)
      }
    },
  })

  return (
    <div>
      <form
        id="donation-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2"
      >
        {!internal && (
          <h1 className="text-xl font-bold justify-self-center">
            Expenses Reimbursement Form
          </h1>
        )}
        {/* Watch donorId changes and auto-update churchId */}
        <form.Field
          name="requester"
          validators={{
            onBlur: ({ value }) => {
              const result =
                ExpenseCreateSchema.shape.requester.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Name"
                  id={field.name}
                  name={field.name}
                  required
                />
              </div>
            )
          }}
        />

        <form.Field
          name="requesterEmail"
          validators={{
            onBlur: ({ value }) => {
              const result =
                ExpenseCreateSchema.shape.requesterEmail.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Email"
                  id={field.name}
                  name={field.name}
                  required
                />
              </div>
            )
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Hidden churchId field for form validation */}
          <form.Field
            name="churchId"
            validators={{
              onBlur: ({ value }) => {
                const result =
                  ExpenseCreateSchema.shape.churchId.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => (
              <div className="gap-0">
                <AutoCompleteSelect
                  field={field}
                  label="Church"
                  id={field.name}
                  name={field.name}
                  options={churches.map((church) => ({
                    value: church._id,
                    label: church.name,
                  }))}
                  required
                  disabled={isPending}
                />
              </div>
            )}
          />

          <form.Field
            name="date"
            children={(field) => (
              <div className="gap-0">
                <DatePicker
                  field={field}
                  label="Expense Date"
                  placeholder="Select when the expense occurred"
                  id={field.name}
                  name={field.name}
                  startYear={2000}
                  disableFutureDates
                  required
                />
              </div>
            )}
          />
        </div>

        <form.Field
          name="notes"
          validators={{
            onBlur: ({ value }) => {
              const result = ExpenseCreateSchema.shape.notes.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <FormTextarea
                field={field}
                label="Notes"
                id={field.name}
                name={field.name}
                disabled={isPending}
                required
              />
            </div>
          )}
        />

        <form.Field
          name="expenseItems"
          validators={{
            onChange: ({ value }) => {
              const result =
                ExpenseCreateSchema.shape.expenseItems.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <ExpenseItemsTable
                items={field.state.value}
                categories={categories}
                internal={internal}
                disabled={isPending}
                allowDelete={!internal}
                onChange={(items) =>
                  field.handleChange(items as Array<ExpenseItemInput>)
                }
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive mt-2">
                  {field.state.meta.errors[0]}
                </p>
              )}
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => ({
            canSubmit: state.canSubmit,
            values: state.values,
          })}
          children={({ canSubmit, values }) => {
            const hasItems = values.expenseItems.length > 0
            const allFieldsFilled =
              values.requester &&
              values.requesterEmail &&
              values.churchId &&
              values.date &&
              values.notes &&
              hasItems

            const isFormValid = canSubmit && allFieldsFilled && !isPending

            return (
              <LoadingButton
                type="submit"
                className="w-full mt-4"
                isLoading={isPending}
                disabled={!isFormValid}
              >
                {isPending ? 'Submitting...' : 'Submit'}
              </LoadingButton>
            )
          }}
        />
      </form>
    </div>
  )
}
